<template>
  <UIDialogActions
    :is-open
    :z-index
    :actions
    :title
    size="md"
    @close="handleClose"
  >
    <span class="text-base sm:text-sm">{{ confirmMessage }}</span>
  </UIDialogActions>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  DialogActionsButton,
  RepositoryInvite,
  RepositoryCollaborator,
  RepositoryStatus,
  ModalClass,
} from '@types'

import { handleCatchedError } from '@/helpers/common'

import { useRepositoriesStore } from '@/store/repositories/index'
import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'

import { UIDialogActions } from '@ui/modals'


type Props = {
  modal: ModalClass<any>
  item: RepositoryInvite | RepositoryCollaborator
  isOwnRecord?: boolean
  repositoryId: string
}

const props = defineProps<Props>()

const repositoriesStore = useRepositoriesStore()
const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const label = computed(() => (props.isOwnRecord ? 'Remove' : 'Unlink'))

const title = computed(() =>
  props.isOwnRecord ? 'Remove collaborator' : 'Unlink repository',
)

const confirmMessage = computed(() =>
  props.isOwnRecord
    ? 'Are you sure you want to remove this collaborator?'
    : 'Are you sure you want to unlink this repository?',
)

const actions = computed<DialogActionsButton[]>(() => [
  {
    label: label.value,
    onClick: handleRemoveAccess,
    buttonVariant: 'danger',
  },
])

const handleClose = () => {
  props.modal.close()
}

const handleRemoveAccess = async () => {
  handleClose()
  try {
    if (props.item.status === RepositoryStatus.ACCEPTED) {
      const data = props.item as RepositoryCollaborator
      await repositoriesCollaboratorsStore.destroy(
        data.email,
        data.user.id,
        props.repositoryId,
      )
      if (!props.isOwnRecord) {
        repositoriesStore.remove(props.repositoryId)
      }
    } else {
      await repositoriesCollaboratorsStore.removeInvite(
        (props.item as RepositoryInvite).id,
      )
    }
    await repositoriesStore.decreaseColaboratorsCount(props.repositoryId)
  } catch (e) {
    handleCatchedError(e as string, {
      id: (props.item as RepositoryInvite).id || props.item.email,
    })
  }
}
</script>
